import { useState, useEffect, useMemo } from "react";
import { Loader2, Trash2, Inbox, Sparkles, Plus, Pencil, Check, ArrowUp, ArrowDown, Eye, EyeOff, Briefcase, Wand2, CheckCheck, ThumbsUp, ThumbsDown, MapPin, Tags } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { coverageLine } from "@/lib/coverage";
import { useCategories } from "@/context/CategoriesContext";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { notifyAdminBadgesDirty, parseFuncties } from "./shared/format";
import { LoadingRow, EmptyState } from "./shared/ui";

// -------- F2: Suggestie-inbox (AI proposes, admin decides) --------
const SUGGESTION_SOURCES = ["ai", "ai-brein", "trefwoord"];

function SuggestionInbox({ onActivated, reloadKey }) {
  const { categories: categoryList, colorOf } = useCategories();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [editing, setEditing] = useState({}); // id -> {text, category, functies}

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/question-suggestions");
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [reloadKey]);

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/admin/gap-scan/run");
      if (data?.reason === "cap_reached") {
        toast.message("Voorstel-limiet bereikt — beoordeel eerst de openstaande suggesties.");
      } else if (data?.reason === "ok" && data?.added > 0) {
        toast.success(`${data.added} nieuwe vragen voorgesteld — bekijk ze in de suggestie-inbox.`);
        await load();
        notifyAdminBadgesDirty();
      } else if (data?.reason === "error") {
        toast.error("Genereren mislukt — probeer het zo nog eens.");
      } else {
        toast.message("Geen duidelijke gaten gevonden.");
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setGenerating(false);
    }
  };

  const startEdit = (q) =>
    setEditing((p) => ({ ...p, [q.id]: { text: q.text, category: q.category ?? "__NONE__", functies: (q.functies || []).join(", ") } }));
  const cancelEdit = (id) => setEditing((p) => { const n = { ...p }; delete n[id]; return n; });

  const saveEdit = async (id) => {
    const d = editing[id];
    if (!d || !d.text.trim()) { toast.error("Tekst mag niet leeg zijn."); return; }
    setSavingId(id);
    try {
      const body = { text: d.text.trim(), category: d.category === "__NONE__" ? null : d.category, functies: parseFuncties(d.functies) };
      const { data } = await api.patch(`/admin/questions/${id}`, body);
      setItems((prev) => prev.map((q) => (q.id === id ? data : q)));
      cancelEdit(id);
      toast.success("Suggestie bijgewerkt.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingId(null);
    }
  };

  const activate = async (q) => {
    setSavingId(q.id);
    try {
      await api.patch(`/admin/questions/${q.id}`, { active: true });
      setItems((prev) => prev.filter((x) => x.id !== q.id));
      toast.success("Suggestie geactiveerd — staat nu in de rotatie.");
      onActivated?.();
      notifyAdminBadgesDirty();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingId(null);
    }
  };

  const remove = async (q) => {
    if (!window.confirm(`Suggestie verwijderen?\n\n"${q.text.slice(0, 90)}${q.text.length > 90 ? "…" : ""}"`)) return;
    setSavingId(q.id);
    try {
      await api.delete(`/admin/questions/${q.id}/hard`);
      setItems((prev) => prev.filter((x) => x.id !== q.id));
      toast.success("Suggestie verwijderd.");
      notifyAdminBadgesDirty();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingId(null);
    }
  };

  const activateAll = async () => {
    if (items.length === 0) return;
    if (!window.confirm(`Alle ${items.length} suggesties activeren en in de rotatie zetten?`)) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/admin/question-suggestions/activate-all");
      setItems([]);
      toast.success(`${data.activated} suggesties geactiveerd.`);
      onActivated?.();
      notifyAdminBadgesDirty();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBulkBusy(false);
    }
  };

  return (
    <div className="mb-6 border border-tw-yellow/30 bg-tw-yellow/5 p-4" data-testid="admin-suggestions-inbox">
      <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
        <div className="font-mono text-[10px] tracking-[0.3em] text-tw-yellow uppercase flex items-center gap-1.5">
          <Sparkles size={12} strokeWidth={2.5} /> Suggesties
          <span data-testid="admin-suggestions-count" className="text-white">({items.length})</span>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={generate}
            disabled={generating}
            data-testid="admin-generate-questions-btn"
            className="h-9 rounded-none bg-tw-yellow text-black hover:bg-[#E6C200] uppercase tracking-[0.15em] text-[10px] font-bold px-3 disabled:opacity-50"
          >
            {generating ? <Loader2 className="animate-spin mr-1" size={12} /> : <Wand2 size={12} className="mr-1" strokeWidth={2.8} />}
            Genereer vragen
          </Button>
          {items.length > 0 && (
            <Button
              onClick={activateAll}
              disabled={bulkBusy}
              data-testid="admin-suggestions-activate-all"
              variant="outline"
              className="h-9 rounded-none bg-transparent border-tw-yellow text-tw-yellow hover:bg-tw-yellow/10 uppercase tracking-[0.15em] text-[10px] font-bold px-3 disabled:opacity-50"
            >
              {bulkBusy ? <Loader2 className="animate-spin mr-1" size={12} /> : <CheckCheck size={12} className="mr-1" strokeWidth={2.8} />}
              Alles activeren
            </Button>
          )}
        </div>
      </div>

      <p className="text-[11px] text-tw-muted leading-relaxed mb-3">
        De AI stelt kennisvragen voor (gaten-radar draait ook automatisch). Beoordeel, pas aan en activeer — pas dan komen ze in de rotatie.
      </p>

      {loading ? (
        <div className="flex items-center gap-2 text-tw-muted font-mono text-xs py-6 justify-center">
          <Loader2 className="animate-spin" size={14} /> LADEN…
        </div>
      ) : items.length === 0 ? (
        <div className="border border-dashed border-tw py-8 flex flex-col items-center text-center" data-testid="admin-suggestions-empty">
          <Inbox size={28} className="text-tw-muted mb-2" />
          <div className="text-white text-sm font-medium">Geen openstaande suggesties</div>
          <div className="text-tw-muted text-xs mt-1 max-w-sm">Genereer er zelf een reeks met de knop hierboven, of wacht op de wekelijkse gaten-radar.</div>
        </div>
      ) : (
        <div className="space-y-2" data-testid="admin-suggestions-list">
          {items.map((q) => {
            const isEditing = editing[q.id] !== undefined;
            const draft = editing[q.id];
            const catColor = q.category ? colorOf(q.category) : "#525252";
            return (
              <article key={q.id} data-testid={`admin-suggestion-${q.id}`} className="bg-tw-surface border border-tw p-3">
                {isEditing ? (
                  <div className="space-y-2">
                    <Textarea
                      value={draft.text} rows={2}
                      onChange={(e) => setEditing((p) => ({ ...p, [q.id]: { ...p[q.id], text: e.target.value } }))}
                      data-testid={`admin-suggestion-edit-text-${q.id}`}
                      className="bg-tw-black border-tw text-white rounded-none focus-visible:ring-0 focus:border-tw-yellow resize-none"
                    />
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <Select value={draft.category} onValueChange={(v) => setEditing((p) => ({ ...p, [q.id]: { ...p[q.id], category: v } }))}>
                        <SelectTrigger data-testid={`admin-suggestion-edit-category-${q.id}`} className="bg-tw-black border-tw text-white h-9 rounded-none focus:ring-0 focus:border-tw-yellow">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-tw-surface border-tw text-white rounded-none">
                          <SelectItem value="__NONE__" className="focus:bg-tw-yellow focus:text-black rounded-none">Geen categorie</SelectItem>
                          {categoryList.map((c) => (
                            <SelectItem key={c.name} value={c.name} className="focus:bg-tw-yellow focus:text-black rounded-none uppercase tracking-wider">{c.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input
                        value={draft.functies}
                        onChange={(e) => setEditing((p) => ({ ...p, [q.id]: { ...p[q.id], functies: e.target.value } }))}
                        placeholder="Functies (leeg = iedereen)"
                        data-testid={`admin-suggestion-edit-functies-${q.id}`}
                        className="bg-tw-black border-tw text-white h-9 rounded-none focus-visible:ring-0 focus:border-tw-yellow"
                      />
                    </div>
                    <div className="flex gap-2 justify-end">
                      <button onClick={() => cancelEdit(q.id)} className="text-[10px] uppercase tracking-widest text-tw-muted hover:text-white px-2 py-1 border border-tw">Annuleer</button>
                      <button onClick={() => saveEdit(q.id)} disabled={savingId === q.id} data-testid={`admin-suggestion-save-${q.id}`}
                        className="text-[10px] uppercase tracking-widest text-black bg-tw-yellow hover:bg-[#E6C200] font-bold px-3 py-1 flex items-center gap-1">
                        {savingId === q.id ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} strokeWidth={3} />} Opslaan
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm leading-snug">{q.text}</p>
                      <div className="mt-2 flex items-center gap-2 flex-wrap">
                        <span
                          data-testid={`admin-suggestion-source-${q.id}`}
                          className="inline-flex items-center gap-0.5 px-1 py-0.5 text-[9px] font-bold tracking-[0.1em] text-black bg-tw-yellow"
                          title={q.source === "ai-brein" ? "Voorgesteld via 'Vraag het brein'" : q.source === "trefwoord" ? "Gemaakt van een trefwoord" : "Voorgesteld door de gaten-radar"}
                        >
                          <Sparkles size={9} strokeWidth={3} /> {q.source === "ai-brein" ? "BREIN" : q.source === "trefwoord" ? "TREFWOORD" : "AI"}
                        </span>
                        {q.source === "trefwoord" && q.keyword_word && (
                          <span data-testid={`admin-suggestion-origin-${q.id}`} className="font-mono text-[10px] text-tw-muted">
                            Trefwoord: {q.keyword_word}{q.keyword_by ? ` · door ${q.keyword_by}` : ""}
                          </span>
                        )}
                        {q.category ? (
                          <span className="inline-block px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.2em] text-black" style={{ backgroundColor: catColor }}>{q.category}</span>
                        ) : (
                          <span className="font-mono text-[9px] tracking-[0.25em] text-tw-muted">GEEN CATEGORIE</span>
                        )}
                        {(q.functies || []).length > 0 ? (
                          (q.functies || []).map((f) => (
                            <span key={f} className="inline-flex items-center gap-1 border border-tw-yellow/60 text-tw-yellow px-1.5 py-0.5 font-mono text-[9px] tracking-[0.2em] uppercase">
                              <Briefcase size={9} strokeWidth={2.8} /> {f}
                            </span>
                          ))
                        ) : (
                          <span className="font-mono text-[9px] tracking-[0.25em] text-tw-muted">IEDEREEN</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button onClick={() => activate(q)} disabled={savingId === q.id} data-testid={`admin-suggestion-activate-${q.id}`}
                        title="Activeren" aria-label="Activeren" className="p-2 text-tw-muted hover:text-tw-yellow transition-colors disabled:opacity-40">
                        {savingId === q.id ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} strokeWidth={2.8} />}
                      </button>
                      <button onClick={() => startEdit(q)} data-testid={`admin-suggestion-edit-${q.id}`}
                        title="Bewerk" aria-label="Bewerk" className="p-2 text-tw-muted hover:text-tw-yellow transition-colors">
                        <Pencil size={14} strokeWidth={2.5} />
                      </button>
                      <button onClick={() => remove(q)} data-testid={`admin-suggestion-delete-${q.id}`}
                        title="Verwijderen" aria-label="Verwijderen" className="p-2 text-tw-muted hover:text-tw-red transition-colors">
                        <Trash2 size={14} strokeWidth={2.5} />
                      </button>
                    </div>
                  </div>
                )}
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}

// -------- Questions Admin --------
// -------- Spark#1: Feedback op antwoorden — admin signal (down-voted answers) --------
function AnswerFeedbackAdmin() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/answer-feedback");
      setItems(data);
    } catch { /* fail-soft */ } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  if (loading || items.length === 0) return null;
  return (
    <div className="mb-6 bg-tw-surface border border-tw-red/40 p-4 sm:p-5" data-testid="answer-feedback-admin-panel">
      <div className="flex items-center gap-2 mb-3">
        <ThumbsDown size={14} className="text-tw-red" strokeWidth={2.5} />
        <div className="font-mono text-[10px] tracking-[0.3em] text-tw-muted">ANTWOORDEN MET NEGATIEVE FEEDBACK</div>
      </div>
      <ul className="divide-y divide-tw" data-testid="answer-feedback-admin-list">
        {items.map((it, i) => (
          <li key={`${it.question}-${i}`} className="py-2.5 flex items-center justify-between gap-3" data-testid={`answer-feedback-row-${i}`}>
            <div className="min-w-0">
              <p className="text-white text-sm truncate">{it.question}</p>
              <p className="font-mono text-[9px] text-tw-muted uppercase tracking-[0.15em]">{it.category}</p>
            </div>
            <div className="flex items-center gap-3 shrink-0 font-mono text-[11px]">
              <span className="text-tw-green flex items-center gap-1"><ThumbsUp size={11} strokeWidth={2.5} /> {it.up}</span>
              <span className="text-tw-red flex items-center gap-1"><ThumbsDown size={11} strokeWidth={2.5} /> {it.down}</span>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function QuestionsAdmin() {
  const { categories: categoryList, colorOf } = useCategories();
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [editing, setEditing] = useState({}); // id -> {text, category, order}
  const [showNew, setShowNew] = useState(false);
  const [newQ, setNewQ] = useState({ text: "", category: "__NONE__", order: 100, functies: "" });
  const [creating, setCreating] = useState(false);
  // Round K — dekking per locatie for the one-line summary under a site question.
  const [coverage, setCoverage] = useState({});
  const loadCoverage = async () => {
    try {
      const { data } = await api.get("/admin/coverage");
      setCoverage(Object.fromEntries((data.questions || []).map((r) => [r.id, r])));
    } catch {
      setCoverage({});
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/questions");
      setQuestions(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    loadCoverage();
  }, []);

  const toggleVaries = async (q) => {
    const result = await patchQuestion(q.id, { varies_by_site: !q.varies_by_site });
    if (!result) return;
    toast.success(result.varies_by_site ? "Verschilt per locatie: aan." : "Verschilt per locatie: uit.");
    loadCoverage();
  };

  const startEdit = (q) => {
    setEditing((prev) => ({
      ...prev,
      [q.id]: {
        text: q.text,
        category: q.category ?? "__NONE__",
        order: q.order,
        functies: (q.functies || []).join(", "),
      },
    }));
  };

  const cancelEdit = (id) => {
    setEditing((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  const patchQuestion = async (id, body) => {
    setSavingId(id);
    try {
      const { data } = await api.patch(`/admin/questions/${id}`, body);
      setQuestions((prev) => prev.map((q) => (q.id === id ? data : q)));
      return data;
    } catch (e) {
      toast.error(formatApiError(e));
      return null;
    } finally {
      setSavingId(null);
    }
  };

  const saveEdit = async (id) => {
    const draft = editing[id];
    if (!draft) return;
    const body = {
      text: draft.text.trim(),
      category: draft.category === "__NONE__" ? null : draft.category,
      order: Number(draft.order) || 0,
      functies: parseFuncties(draft.functies),
    };
    if (!body.text) {
      toast.error("Tekst mag niet leeg zijn.");
      return;
    }
    const result = await patchQuestion(id, body);
    if (result) {
      cancelEdit(id);
      toast.success("Vraag bijgewerkt.");
    }
  };

  const [inboxReload, setInboxReload] = useState(0);

  const toggleActive = async (q) => {
    const result = await patchQuestion(q.id, { active: !q.active });
    if (!result) return;  // PATCH failed — don't toast or reload the inbox
    const isAiSuggestion = SUGGESTION_SOURCES.includes(q.source);
    if (q.active && isAiSuggestion) {
      // Deactivating an AI question sends it back to the Suggestie-inbox (main list hides it).
      setInboxReload((n) => n + 1);
      toast.success("Teruggezet naar suggesties.");
    } else {
      toast.success(q.active ? "Vraag gedeactiveerd." : "Vraag geactiveerd.");
    }
  };

  const hardDelete = async (q) => {
    if (q.active) {
      toast.error("Deactiveer de vraag eerst.");
      return;
    }
    if (!window.confirm(`Vraag definitief verwijderen?\n\n"${q.text.slice(0, 90)}${q.text.length > 90 ? "…" : ""}"`)) return;
    try {
      await api.delete(`/admin/questions/${q.id}/hard`);
      setQuestions((prev) => prev.filter((x) => x.id !== q.id));
      toast.success("Vraag verwijderd.");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const nudgeOrder = async (q, delta) => {
    await patchQuestion(q.id, { order: (q.order || 0) + delta });
  };

  const createQuestion = async () => {
    if (!newQ.text.trim()) {
      toast.error("Tekst mag niet leeg zijn.");
      return;
    }
    setCreating(true);
    try {
      const body = {
        text: newQ.text.trim(),
        category: newQ.category === "__NONE__" ? null : newQ.category,
        order: Number(newQ.order) || 0,
        active: true,
        functies: parseFuncties(newQ.functies),
      };
      const { data } = await api.post("/admin/questions", body);
      setQuestions((prev) => [...prev, data].sort((a, b) => a.order - b.order));
      setNewQ({ text: "", category: "__NONE__", order: 100, functies: "" });
      setShowNew(false);
      toast.success("Vraag toegevoegd.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCreating(false);
    }
  };

  const mainQuestions = useMemo(
    () => questions.filter((q) => !(q.active === false && SUGGESTION_SOURCES.includes(q.source))),
    [questions]
  );
  // Round P — client-side filter; the manual order (arrows on q.order) is untouched.
  const [onlyOpen, setOnlyOpen] = useState(false);
  const shownQuestions = useMemo(
    () => (onlyOpen ? mainQuestions.filter((q) => q.active && q.answer_count === 0) : mainQuestions),
    [mainQuestions, onlyOpen]
  );
  const openQuestionsCount = useMemo(
    () => mainQuestions.filter((q) => q.active && q.has_memo === false).length,
    [mainQuestions]
  );

  return (
    <div data-testid="admin-vragen-panel">
      <AnswerFeedbackAdmin />
      <SuggestionInbox onActivated={load} reloadKey={inboxReload} />

      <div className="flex flex-wrap items-center justify-between gap-y-2 mb-4">
        <div className="font-mono text-[10px] tracking-[0.3em] text-tw-muted">KENNISVRAGEN</div>
        <div className="flex flex-wrap items-center justify-end gap-x-4 gap-y-2">
          {openQuestionsCount > 0 && (
            <span className="font-mono text-[10px] text-tw-muted uppercase tracking-[0.15em]" data-testid="admin-vragen-open-count">
              {openQuestionsCount} nog niet beantwoord
            </span>
          )}
          <button type="button" onClick={() => setOnlyOpen((v) => !v)} aria-pressed={onlyOpen} data-testid="admin-vragen-only-open"
            className={`tw-press font-mono text-[10px] uppercase tracking-[0.15em] px-2 py-1 border transition-colors duration-150 ${onlyOpen ? "bg-tw-yellow text-black border-tw-yellow" : "text-tw-muted border-tw hover:text-white hover:border-tw-yellow"}`}>
            Alleen onbeantwoord
          </button>
          <div className="font-mono text-sm text-tw-yellow" data-testid="admin-vragen-count">
            {mainQuestions.length}
          </div>
          <Button
            onClick={() => setShowNew((v) => !v)}
            data-testid="admin-vragen-toggle-new"
            className="h-9 rounded-none bg-tw-yellow text-black hover:bg-[#E6C200] uppercase tracking-[0.2em] text-[10px] font-bold px-3"
          >
            <Plus size={12} className="mr-1" strokeWidth={3} /> Nieuw
          </Button>
        </div>
      </div>

      {showNew && (
        <div className="bg-tw-surface border border-tw-yellow p-4 mb-4 space-y-3 tw-fadeup" data-testid="admin-vragen-new-form">
          <div className="font-mono text-[10px] tracking-[0.3em] text-tw-yellow">NIEUWE VRAAG</div>
          <Textarea
            value={newQ.text}
            onChange={(e) => setNewQ((p) => ({ ...p, text: e.target.value }))}
            placeholder="Stel het team een specifieke, nuttige vraag…"
            rows={3}
            data-testid="admin-vragen-new-text"
            className="bg-tw-black border-tw text-white rounded-none focus-visible:ring-0 focus-visible:ring-offset-0 focus:border-tw-yellow resize-none"
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Select
              value={newQ.category}
              onValueChange={(v) => setNewQ((p) => ({ ...p, category: v }))}
            >
              <SelectTrigger
                data-testid="admin-vragen-new-category"
                className="bg-tw-black border-tw text-white h-10 rounded-none focus:ring-0 focus:ring-offset-0 focus:border-tw-yellow"
              >
                <SelectValue placeholder="Categorie (optioneel)" />
              </SelectTrigger>
              <SelectContent className="bg-tw-surface border-tw text-white rounded-none">
                <SelectItem value="__NONE__" className="focus:bg-tw-yellow focus:text-black rounded-none">
                  Geen categorie
                </SelectItem>
                {categoryList.map((c) => (
                  <SelectItem
                    key={c.name}
                    value={c.name}
                    className="focus:bg-tw-yellow focus:text-black rounded-none uppercase tracking-wider"
                  >
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              type="number"
              value={newQ.order}
              onChange={(e) => setNewQ((p) => ({ ...p, order: e.target.value }))}
              placeholder="Volgorde"
              data-testid="admin-vragen-new-order"
              className="bg-tw-black border-tw text-white h-10 rounded-none focus-visible:ring-0 focus-visible:ring-offset-0 focus:border-tw-yellow"
            />
          </div>
          <div>
            <Input
              value={newQ.functies}
              onChange={(e) => setNewQ((p) => ({ ...p, functies: e.target.value }))}
              placeholder="Zichtbaar voor functies (leeg = iedereen). Bv: TD, Marketing"
              data-testid="admin-vragen-new-functies"
              className="bg-tw-black border-tw text-white h-10 rounded-none focus-visible:ring-0 focus-visible:ring-offset-0 focus:border-tw-yellow"
            />
            <p className="mt-1.5 text-[10px] text-tw-muted font-mono tracking-wide">
              Komma-gescheiden. Leeg = zichtbaar voor iedereen.
            </p>
          </div>
          <div className="flex gap-2 justify-end">
            <Button
              onClick={() => {
                setShowNew(false);
                setNewQ({ text: "", category: "__NONE__", order: 100, functies: "" });
              }}
              variant="outline"
              className="h-9 rounded-none bg-transparent border-tw text-white hover:bg-tw-surface-hover hover:text-white uppercase tracking-[0.2em] text-[10px]"
            >
              Annuleer
            </Button>
            <Button
              onClick={createQuestion}
              disabled={creating || !newQ.text.trim()}
              data-testid="admin-vragen-new-save"
              className="h-9 rounded-none bg-tw-yellow text-black hover:bg-[#E6C200] uppercase tracking-[0.2em] text-[10px] font-bold disabled:opacity-50"
            >
              {creating ? <Loader2 className="animate-spin" size={14} /> : "Opslaan"}
            </Button>
          </div>
        </div>
      )}

      {loading ? (
        <LoadingRow />
      ) : mainQuestions.length === 0 ? (
        <EmptyState label="Nog geen vragen" hint="Voeg er één toe om het team op weg te helpen." testId="admin-vragen-empty" />
      ) : (
        <div className="space-y-2" data-testid="admin-vragen-list">
          {onlyOpen && shownQuestions.length === 0 && (
            <p className="font-mono text-[11px] text-tw-muted py-4" data-testid="admin-vragen-only-open-empty">Elke actieve vraag heeft al een antwoord.</p>
          )}
          {shownQuestions.map((q, i) => {
            const isEditing = editing[q.id] !== undefined;
            const draft = editing[q.id];
            const catColor = q.category ? colorOf(q.category) : "#525252";
            return (
              <article
                key={q.id}
                data-testid={`admin-question-${q.id}`}
                className={`tw-fadeup bg-tw-surface border p-4 transition-colors duration-150 ${
                  q.active ? "border-tw hover:border-tw-yellow" : "border-tw opacity-60"
                }`}
                style={{ animationDelay: `${Math.min(i * 20, 240)}ms` }}
              >
                <div className="flex items-start gap-3">
                  <div className="flex flex-col items-center gap-1 pt-1 shrink-0">
                    <button
                      onClick={() => nudgeOrder(q, -1)}
                      disabled={savingId === q.id}
                      data-testid={`admin-question-up-${q.id}`}
                      className="p-0.5 text-tw-muted hover:text-tw-yellow transition-colors disabled:opacity-40"
                      aria-label="Move up"
                    >
                      <ArrowUp size={14} strokeWidth={2.5} />
                    </button>
                    <span className="font-mono text-[10px] text-tw-muted">{q.order}</span>
                    <button
                      onClick={() => nudgeOrder(q, 1)}
                      disabled={savingId === q.id}
                      data-testid={`admin-question-down-${q.id}`}
                      className="p-0.5 text-tw-muted hover:text-tw-yellow transition-colors disabled:opacity-40"
                      aria-label="Omlaag"
                    >
                      <ArrowDown size={14} strokeWidth={2.5} />
                    </button>
                  </div>

                  <div className="flex-1 min-w-0">
                    {isEditing ? (
                      <div className="space-y-2">
                        <Textarea
                          value={draft.text}
                          onChange={(e) =>
                            setEditing((prev) => ({
                              ...prev,
                              [q.id]: { ...prev[q.id], text: e.target.value },
                            }))
                          }
                          rows={2}
                          data-testid={`admin-question-edit-text-${q.id}`}
                          className="bg-tw-black border-tw text-white rounded-none focus-visible:ring-0 focus-visible:ring-offset-0 focus:border-tw-yellow resize-none"
                        />
                        <div className="grid grid-cols-2 gap-2">
                          <Select
                            value={draft.category}
                            onValueChange={(v) =>
                              setEditing((prev) => ({
                                ...prev,
                                [q.id]: { ...prev[q.id], category: v },
                              }))
                            }
                          >
                            <SelectTrigger
                              data-testid={`admin-question-edit-category-${q.id}`}
                              className="bg-tw-black border-tw text-white h-9 rounded-none focus:ring-0 focus:ring-offset-0 focus:border-tw-yellow"
                            >
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-tw-surface border-tw text-white rounded-none">
                              <SelectItem value="__NONE__" className="focus:bg-tw-yellow focus:text-black rounded-none">
                                Geen categorie
                              </SelectItem>
                              {categoryList.map((c) => (
                                <SelectItem
                                  key={c.name}
                                  value={c.name}
                                  className="focus:bg-tw-yellow focus:text-black rounded-none uppercase tracking-wider"
                                >
                                  {c.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Input
                            type="number"
                            value={draft.order}
                            onChange={(e) =>
                              setEditing((prev) => ({
                                ...prev,
                                [q.id]: { ...prev[q.id], order: e.target.value },
                              }))
                            }
                            data-testid={`admin-question-edit-order-${q.id}`}
                            className="bg-tw-black border-tw text-white h-9 rounded-none focus-visible:ring-0 focus-visible:ring-offset-0 focus:border-tw-yellow"
                          />
                        </div>
                        <Input
                          value={draft.functies}
                          onChange={(e) =>
                            setEditing((prev) => ({
                              ...prev,
                              [q.id]: { ...prev[q.id], functies: e.target.value },
                            }))
                          }
                          placeholder="Functies (komma-gescheiden, leeg = iedereen)"
                          data-testid={`admin-question-edit-functies-${q.id}`}
                          className="bg-tw-black border-tw text-white h-9 rounded-none focus-visible:ring-0 focus-visible:ring-offset-0 focus:border-tw-yellow"
                        />
                        <div className="flex gap-2 justify-end">
                          <button
                            onClick={() => cancelEdit(q.id)}
                            className="text-[10px] uppercase tracking-widest text-tw-muted hover:text-white px-2 py-1 border border-tw"
                          >
                            Annuleer
                          </button>
                          <button
                            onClick={() => saveEdit(q.id)}
                            disabled={savingId === q.id}
                            data-testid={`admin-question-save-${q.id}`}
                            className="text-[10px] uppercase tracking-widest text-black bg-tw-yellow hover:bg-[#E6C200] font-bold px-3 py-1 flex items-center gap-1"
                          >
                            {savingId === q.id ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} strokeWidth={3} />}
                            Opslaan
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <p className="text-white text-sm leading-snug">{q.text}</p>
                        <div className="mt-2 flex items-center gap-2 flex-wrap">
                          {q.source === "ai" && (
                            <span
                              data-testid={`admin-question-ai-badge-${q.id}`}
                              className="inline-flex items-center gap-0.5 px-1 py-0.5 text-[9px] font-bold tracking-[0.1em] text-black bg-tw-yellow"
                              title="Automatisch voorgesteld door de gaten-radar"
                            >
                              <Sparkles size={9} strokeWidth={3} /> AI
                            </span>
                          )}
                          {q.source === "trefwoord" && (
                            <span
                              data-testid={`admin-question-keyword-badge-${q.id}`}
                              className="inline-flex items-center gap-0.5 px-1 py-0.5 text-[9px] font-bold tracking-[0.1em] uppercase text-black bg-tw-yellow"
                              title={q.keyword_by ? `Trefwoord toegevoegd door ${q.keyword_by}` : "Gemaakt van een trefwoord"}
                            >
                              <Tags size={9} strokeWidth={3} /> {q.keyword_deleted || !q.keyword_word ? "Trefwoord verwijderd" : `Trefwoord: ${q.keyword_word}`}
                            </span>
                          )}
                          {q.source === "ai-brein" && (
                            <span
                              data-testid={`admin-question-brein-badge-${q.id}`}
                              className="inline-flex items-center gap-0.5 px-1 py-0.5 text-[9px] font-bold tracking-[0.1em] text-black bg-tw-yellow"
                              title="Voorgesteld via 'Vraag het brein'"
                            >
                              <Sparkles size={9} strokeWidth={3} /> BREIN
                            </span>
                          )}
                          {q.category ? (
                            <span
                              className="inline-block px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.2em] text-black"
                              style={{ backgroundColor: catColor }}
                            >
                              {q.category}
                            </span>
                          ) : (
                            <span className="font-mono text-[9px] tracking-[0.25em] text-tw-muted">GEEN CATEGORIE</span>
                          )}
                          {(q.functies || []).length > 0 ? (
                            (q.functies || []).map((f) => (
                              <span
                                key={f}
                                data-testid={`admin-question-functie-${q.id}-${f}`}
                                className="inline-flex items-center gap-1 border border-tw-yellow/60 text-tw-yellow px-1.5 py-0.5 font-mono text-[9px] tracking-[0.2em] uppercase"
                              >
                                <Briefcase size={9} strokeWidth={2.8} /> {f}
                              </span>
                            ))
                          ) : (
                            <span className="font-mono text-[9px] tracking-[0.25em] text-tw-muted">
                              IEDEREEN
                            </span>
                          )}
                          {!q.active && (
                            <span className="font-mono text-[9px] tracking-[0.25em] text-tw-red">INACTIEF</span>
                          )}
                          {typeof q.answer_count === "number" && (
                            <span data-testid={`admin-question-answers-${q.id}`}
                              className={`font-mono text-[9px] tracking-[0.2em] uppercase px-1.5 py-0.5 border ${q.answer_count === 0 ? "border-tw-red/60 text-tw-red" : "border-emerald-500/60 text-emerald-400"}`}>
                              {q.answer_count === 0 ? "open" : q.answer_count === 1 ? "1 antwoord" : `${q.answer_count} antwoorden`}
                            </span>
                          )}
                          <button
                            type="button"
                            onClick={() => toggleVaries(q)}
                            disabled={savingId === q.id}
                            aria-pressed={!!q.varies_by_site}
                            data-testid={`admin-question-varies-${q.id}`}
                            title="Zet aan als het antwoord per vestiging anders is"
                            className={`tw-press inline-flex items-center gap-1 px-1.5 py-0.5 border font-mono text-[9px] tracking-[0.2em] uppercase transition-colors duration-150 disabled:opacity-40 ${q.varies_by_site ? "bg-tw-yellow text-black border-tw-yellow" : "text-tw-muted border-tw hover:text-white hover:border-tw-yellow"}`}
                          >
                            <MapPin size={9} strokeWidth={2.8} /> Verschilt per locatie
                          </button>
                        </div>
                        {q.varies_by_site && coverage[q.id] && (
                          <p className="mt-2 font-mono text-[10px] tracking-[0.05em] text-tw-muted" data-testid={`admin-question-coverage-${q.id}`}>
                            {coverageLine(coverage[q.id])}
                          </p>
                        )}
                      </>
                    )}
                  </div>

                  {!isEditing && (
                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        onClick={() => toggleActive(q)}
                        disabled={savingId === q.id}
                        data-testid={`admin-question-toggle-${q.id}`}
                        className="p-2 text-tw-muted hover:text-tw-yellow transition-colors disabled:opacity-40"
                        title={q.active ? "Deactiveer" : "Activeer"}
                        aria-label={q.active ? "Deactiveer" : "Activeer"}
                      >
                        {q.active ? <Eye size={14} strokeWidth={2.5} /> : <EyeOff size={14} strokeWidth={2.5} />}
                      </button>
                      <button
                        onClick={() => startEdit(q)}
                        data-testid={`admin-question-edit-${q.id}`}
                        className="p-2 text-tw-muted hover:text-tw-yellow transition-colors"
                        title="Bewerk"
                        aria-label="Bewerk"
                      >
                        <Pencil size={14} strokeWidth={2.5} />
                      </button>
                      {!q.active && (
                        <button
                          onClick={() => hardDelete(q)}
                          data-testid={`admin-question-hard-delete-${q.id}`}
                          className="p-2 text-tw-muted hover:text-tw-red transition-colors"
                          title="Definitief verwijderen"
                          aria-label="Definitief verwijderen"
                        >
                          <Trash2 size={14} strokeWidth={2.5} />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}
